import type { CustomIconKey } from 'hooks/usePreferences/customIcons';
import type { GetToolsQuery, ToolInteractionUnion } from 'generated/graphql';
import type { FeatureFlag } from 'modules/feature-flags';

import { useMemo } from 'react';
import { FF, useSplitFlags } from 'modules/feature-flags';
import useAsync from 'react-use/esm/useAsync';
import { client as apolloClient } from 'modules/Apollo/client';
import { GET_TOOLS } from 'modules/Apollo/queries';
import memoize from 'lodash.memoize';
import { ToolCategory, ToolInteractionType } from 'generated/graphql';
import { TOOL_RESTRICTIONS } from 'domains/viewer/ViewportDre/constants';
import { useCheckUserRISPermissions } from 'integrations/RIS/checkPermissions';

export type ToolID = string;

export type BuiltInToolIconName =
  | 'visibility'
  | 'pan_tool'
  | 'zoom_in'
  | 'swap_horizontal'
  | 'swap_vertical'
  | 'exposure'
  | 'straighten'
  | 'share'
  | 'code'
  | 'panorama_fish_eye'
  | 'adjust'
  | 'invert_colors'
  | 'cached'
  | 'find_in_page'
  | 'crop_square'
  | 'play_arrow'
  | 'delete'
  | 'vertical_split'
  | 'location_searching'
  | 'layers'
  | 'border_color'
  | 'font_download'
  | '3d_rotation'
  | 'anatomic_navigator'
  | 'check_circle'
  | 'delete_icon'
  | 'fiber_manual_record'
  | 'skip_previous'
  | 'skip_next'
  | 'manualLinkedScroll'
  | 'automaticLinkedScroll'
  | 'arrow'
  | 'key_image'
  | 'toggle_tool_bar'
  | 'toggle_thumbnail_bar'
  | 'toggle_cad_annotation'
  | 'clear_viewport'
  | string;

export type IconName = CustomIconKey | BuiltInToolIconName | string;

type SDKToolKey = {
  key: string;
  interactionId: string;
};

type BuiltInToolKey = {
  key: string;
};

export const TOOL_CATEGORIES: Partial<Record<ToolCategory, string>> = {
  [ToolCategory.ImageNavigation]: 'Mouse and Tool Wheel Preset',
  [ToolCategory.ImageAdjustment]: 'Image Adjustment',
  [ToolCategory.Annotations]: 'Annotations',
  [ToolCategory.Workflow]: 'Workflow',
  [ToolCategory.Reporter]: 'Reporter',
  [ToolCategory.Layouts]: 'Layouts',
  [ToolCategory.HangingProtocols]: 'Hanging Protocol (HP)',
};

export type Tool = {
  id: ToolID;
  iconName: IconName;
  secondaryIconName?: string;
  name: string;
  isMouseAdaptor: boolean;
  isScrollAdaptor?: boolean;
  hideFromToolbar?: boolean;
  isRightMouseButtonAllowed?: boolean;
  description: string;
  secondaryDescription?: string;
  keys?: Array<SDKToolKey | BuiltInToolKey>;
  splitFlag?: string;
  source?: 'sdk' | 'built-in';
  svgIcon?: string;
  category: ToolCategory;
  lockedKey?: boolean;
  restrictions?: Array<number>;
};

export const builtInTools: {
  [key: string]: Tool;
} = {
  pan: {
    id: 'PAN',
    iconName: 'pan_tool',
    name: 'pan',
    isMouseAdaptor: true,
    isRightMouseButtonAllowed: true,
    description: 'Pan',
    category: ToolCategory.ImageAdjustment,
  },
  zoom: {
    id: 'ZOOM',
    iconName: 'zoom_in',
    name: 'zoom',
    isMouseAdaptor: true,
    isRightMouseButtonAllowed: true,
    description: 'Zoom',
    category: ToolCategory.ImageAdjustment,
  },
  horizontalFlip: {
    id: 'HORIZONTAL_FLIP',
    iconName: 'swap_horizontal',
    name: 'horizontalFlip',
    isMouseAdaptor: false,
    description: 'Horizontal Flip',
    category: ToolCategory.ImageAdjustment,
  },
  verticalFlip: {
    id: 'VERTICAL_FLIP',
    iconName: 'swap_vertical',
    name: 'verticalFlip',
    isMouseAdaptor: false,
    description: 'Vertical Flip',
    category: ToolCategory.ImageAdjustment,
  },
  anatomicNavigator: {
    id: 'ANATOMIC_NAVIGATOR',
    iconName: 'anatomic_navigator',
    name: 'anatomicNavigator',
    isMouseAdaptor: true,
    description: 'Anatomic navigator',
    category: ToolCategory.Annotations,
  },
  wwwc: {
    id: 'WWWC',
    iconName: 'exposure',
    name: 'wwwc',
    isMouseAdaptor: true,
    isRightMouseButtonAllowed: true,
    description: 'W/L',
    category: ToolCategory.ImageAdjustment,
  },
  length: {
    id: 'LENGTH',
    iconName: 'straighten',
    name: 'length',
    isMouseAdaptor: true,
    description: 'Length',
    category: ToolCategory.Annotations,
  },
  angle: {
    id: 'ANGLE',
    iconName: 'share',
    name: 'angle',
    isMouseAdaptor: true,
    description: 'Angle',
    category: ToolCategory.Annotations,
  },
  cobbAngle: {
    id: 'COBB_ANGLE',
    iconName: 'code',
    name: 'cobbAngle',
    isMouseAdaptor: true,
    description: 'Cobb Angle',
    category: ToolCategory.Annotations,
  },
  ellipse: {
    id: 'ELLIPSE',
    iconName: 'panorama_fish_eye',
    name: 'ellipse',
    isMouseAdaptor: true,
    description: 'Ellipse',
    category: ToolCategory.Annotations,
  },
  circle: {
    id: 'CIRCLE',
    iconName: 'adjust',
    name: 'circle',
    isMouseAdaptor: true,
    description: 'Circle',
    category: ToolCategory.Annotations,
  },
  invert: {
    id: 'INVERT',
    iconName: 'invert_colors',
    name: 'invert',
    isMouseAdaptor: false,
    description: 'Invert',
    category: ToolCategory.ImageAdjustment,
  },
  freeRotate: {
    id: 'FREE_ROTATE',
    iconName: 'cached',
    name: 'freeRotate',
    isMouseAdaptor: true,
    isRightMouseButtonAllowed: true,
    description: 'Free Rotate',
    category: ToolCategory.ImageAdjustment,
  },
  magnifyGlass: {
    id: 'MAGNIFY_GLASS',
    iconName: 'find_in_page',
    name: 'magnifyGlass',
    isMouseAdaptor: true,
    description: 'Magnify glass',
    category: ToolCategory.ImageAdjustment,
  },
  rectangle: {
    id: 'RECTANGLE',
    iconName: 'crop_square',
    name: 'rectangle',
    isMouseAdaptor: true,
    description: 'Rect',
    category: ToolCategory.Annotations,
  },
  arrow: {
    id: 'ARROW',
    iconName: 'arrow',
    name: 'arrow',
    isMouseAdaptor: true,
    description: 'Arrow',
    category: ToolCategory.Annotations,
  },
  rotate: {
    id: 'ROTATE',
    iconName: '3d_rotation',
    name: 'rotate',
    isMouseAdaptor: true,
    description: 'Rotate',
    category: ToolCategory.ImageAdjustment,
  },
  rotate90: {
    id: 'ROTATE_90',
    iconName: 'rotate90',
    name: 'rotate90',
    isMouseAdaptor: true,
    description: 'Rotate 90˚',
    category: ToolCategory.ImageAdjustment,
  },
  cine: {
    id: 'CINE',
    iconName: 'play_arrow',
    secondaryIconName: 'stop',
    name: 'cine',
    isMouseAdaptor: false,
    description: 'Cine',
    secondaryDescription: 'Stop Cine',
    category: ToolCategory.ImageNavigation,
  },
  eraser: {
    id: 'ERASER',
    iconName: 'erase',
    name: 'eraser',
    isMouseAdaptor: true,
    description: 'Eraser',
    category: ToolCategory.Annotations,
  },
  eraseAll: {
    id: 'ERASE_ALL',
    iconName: 'delete',
    name: 'eraseAll',
    isMouseAdaptor: true,
    description: 'Erase All',
    category: ToolCategory.Annotations,
  },
  vertebraeLabel: {
    id: 'VERTEBRAE_LABEL',
    iconName: 'vertical_split',
    name: 'vertebraeLabel',
    isMouseAdaptor: true,
    description: 'Label Vertebrae',
    category: ToolCategory.Annotations,
  },
  landmark: {
    id: 'LANDMARK',
    iconName: 'landmark',
    name: 'landmark',
    isMouseAdaptor: true,
    description: 'Landmark',
    category: ToolCategory.Annotations,
  },
  fastScroll: {
    id: 'FAST_SCROLL',
    iconName: 'layers',
    name: 'fastScroll',
    isMouseAdaptor: true,
    isRightMouseButtonAllowed: true,
    description: 'Fast Scroll',
    category: ToolCategory.ImageNavigation,
  },
  regularScroll: {
    id: 'REGULAR_SCROLL',
    iconName: 'regular_scroll',
    name: 'regularScroll',
    isMouseAdaptor: true,
    isRightMouseButtonAllowed: true,
    description: 'Regular Scroll',
    category: ToolCategory.ImageNavigation,
  },
  contour: {
    id: 'CONTOUR',
    iconName: 'border_color',
    name: 'contour',
    isMouseAdaptor: true,
    description: 'Contour',
    category: ToolCategory.Annotations,
  },
  toggleAnnotations: {
    id: 'TOGGLE_ANNOTATIONS',
    iconName: 'font_download',
    name: 'toggleAnnotations',
    isMouseAdaptor: false,
    description: 'Toggle Annotations',
    category: ToolCategory.Annotations,
  },
  jumpTo: {
    id: 'JUMP_TO',
    iconName: 'location_searching',
    name: 'jumpTo',
    isMouseAdaptor: true,
    description: 'Jump To',
    category: ToolCategory.ImageNavigation,
  },
  clearViewport: {
    id: 'CLEAR_VIEWPORT',
    iconName: 'clear_viewport',
    name: 'clearViewport',
    isMouseAdaptor: true,
    description: 'Clear Viewport',
    category: ToolCategory.ImageNavigation,
  },
  reset: {
    id: 'RESET',
    iconName: 'reset',
    name: 'reset',
    isMouseAdaptor: false,
    description: 'Reset',
    category: ToolCategory.ImageAdjustment,
  },
  next_series_set: {
    id: 'NEXT_SET',
    iconName: 'skip_next',
    name: 'next_series_set',
    isMouseAdaptor: false,
    description: 'Next Series',
    category: ToolCategory.ImageAdjustment,
  },
  prev_series_set: {
    id: 'PREV_SET',
    iconName: 'skip_previous',
    name: 'prev_series_set',
    isMouseAdaptor: false,
    description: 'Previous Series',
    category: ToolCategory.ImageAdjustment,
  },
  keyImage: {
    id: 'KEY_IMAGE',
    iconName: 'key_image',
    name: 'keyImage',
    isMouseAdaptor: true,
    description: 'Bookmark',
    splitFlag: FF.IMAGE_BOOKMARK,
    category: ToolCategory.Annotations,
  },
  swapCells: {
    id: 'SWAP_CELLS',
    iconName: 'swap_calls',
    name: 'swapCells',
    isMouseAdaptor: true,
    description: 'Swap Viewports',
    category: ToolCategory.ImageNavigation,
    keys: [{ key: 'shift' }],
    lockedKey: true,
    restrictions: [TOOL_RESTRICTIONS.shortcut_only],
  },
  switchViewType: {
    id: 'SWITCH_VIEWTYPE',
    iconName: 'switch_viewtype',
    name: 'switchViewType',
    isMouseAdaptor: false,
    description: 'View 2D/3D',
    category: ToolCategory.ImageAdjustment,
  },
  fastRuler: {
    id: 'FAST_RULER',
    iconName: 'fast_ruler',
    name: 'fastRuler',
    isMouseAdaptor: true,
    description: 'Fast Ruler',
    category: ToolCategory.Annotations,
    splitFlag: FF.FAST_ANNOTATIONS,
  },
  fastRoi: {
    id: 'FAST_ROI',
    iconName: 'fast_roi',
    name: 'fastRoi',
    isMouseAdaptor: true,
    description: 'Fast ROI',
    category: ToolCategory.Annotations,
    splitFlag: FF.FAST_ANNOTATIONS,
  },

  toggleThumbnailBar: {
    id: 'TOGGLE_THUMBNAIL_BAR',
    iconName: 'toggle_thumbnail_bar',
    name: 'toggleThumbnailBar',
    isMouseAdaptor: false,
    hideFromToolbar: false,
    description: 'Toggle Visibility of Study Thumbnail Bar',
    category: ToolCategory.Workflow,
  },
  toggleToolBar: {
    id: 'TOGGLE_TOOL_BAR',
    iconName: 'toggle_tool_bar',
    name: 'toggleToolBar',
    isMouseAdaptor: false,
    hideFromToolbar: false,
    description: 'Toggle Visibility of Sidenav',
    category: ToolCategory.Workflow,
  },
  toggleCadAnnotation: {
    id: 'TOGGLE_CAD_ANNOTATION',
    iconName: 'toggle_cad_annotation',
    name: 'toggleCadAnnotation',
    isMouseAdaptor: false,
    hideFromToolbar: false,
    description: 'Toggle Visibility of CAD Annotation',
    category: ToolCategory.Workflow,
  },
};

export const visibilityTools: {
  [key: string]: Tool;
} = {
  showAllVisibility: {
    id: 'SHOW_ALL_VISIBILITY',
    name: 'showAllVisibility',
    description: 'Show All',
    keys: [{ key: 'shift+a' }],
    isMouseAdaptor: false,
    iconName: 'visibility',
    category: ToolCategory.Annotations,
  },
  hideAllVisibility: {
    id: 'HIDE_ALL_VISIBILITY',
    name: 'hideAllVisibility',
    description: 'Hide All',
    keys: [{ key: 'shift+h' }],
    isMouseAdaptor: false,
    iconName: 'visibility',
    category: ToolCategory.Annotations,
  },
  toggleAnnotationsVisibility: {
    id: 'TOGGLE_ANNOTATIONS_VISIBILITY',
    name: 'toggleAnnotationsVisibility',
    description: 'Toggle Annotations Visibility',
    keys: [{ key: 'shift+v' }],
    isMouseAdaptor: false,
    iconName: 'visibility',
    category: ToolCategory.Annotations,
  },
  toggleMetadataVisibility: {
    id: 'TOGGLE_METADATA_VISIBILITY',
    name: 'toggleMetadataVisibility',
    description: 'Toggle Metadata',
    keys: [{ key: 'shift+m' }],
    isMouseAdaptor: false,
    iconName: 'visibility',
    category: ToolCategory.Annotations,
  },
  toggleMaskVisibility: {
    id: 'TOGGLE_MASK_VISIBILITY',
    name: 'toggleMaskVisibility',
    description: 'Toggle Masks',
    keys: [{ key: 'shift+s' }],
    isMouseAdaptor: false,
    iconName: 'visibility',
    category: ToolCategory.Annotations,
  },
  toggleGspsAnnotationsVisibility: {
    id: 'TOGGLE_GSPS_ANNOTATIONS_VISIBILITY',
    name: 'toggleGspsAnnotationsVisibility',
    description: 'Toggle GSPS Annotations',
    keys: [],
    isMouseAdaptor: false,
    iconName: 'visibility',
    category: ToolCategory.Annotations,
  },
};

export const scrollLinkTools: {
  [key: string]: Tool;
} = {
  automaticLinkedScroll: {
    id: 'AUTOMATIC_LINKED_SCROLL',
    iconName: 'automaticLinkedScroll',
    name: 'automaticLinkedScroll',
    isMouseAdaptor: false,
    isScrollAdaptor: true,
    description: 'Automatic Scroll',
    category: ToolCategory.ImageNavigation,
  },
  manualLinkedScroll: {
    id: 'MANUAL_LINKED_SCROLL',
    iconName: 'manualLinkedScroll',
    name: 'manualLinkedScroll',
    isMouseAdaptor: false,
    isScrollAdaptor: true,
    description: 'Manual Scroll',
    category: ToolCategory.ImageNavigation,
  },
};

export const allBuiltInTools = {
  ...builtInTools,
  ...visibilityTools,
  ...scrollLinkTools,
} as const;

export const configurableTools = {
  ...builtInTools,
  ...scrollLinkTools,
} as const;

export const getAllTools: () => Promise<Array<Tool>> = memoize(
  async () => {
    const builtInTools = Object.values(allBuiltInTools);
    try {
      const {
        data: { tools: allSdkTools },
      } = await apolloClient.query<GetToolsQuery, never>({ query: GET_TOOLS });

      const sdkTools: Tool[] = allSdkTools.map((tool) => ({
        id: tool.id,
        name: tool.name,
        iconName: tool.name,
        isMouseAdaptor: tool.interactions.some(getSdkToolIsMouseAdaptor),
        description: tool.name,
        svgIcon: tool.icon,
        source: 'sdk',
        keys: getSdkToolKeys(tool.id, tool.interactions),
        category: tool.category,
      }));

      return [...builtInTools, ...sdkTools];
    } catch (e: any) {
      return builtInTools;
    }
  },
  () => 'allTools'
);

const getSdkToolIsMouseAdaptor = (interaction: Readonly<ToolInteractionUnion>): boolean =>
  interaction.type === ToolInteractionType.ViewportClick;

const getSdkToolKeys = (
  toolId: string,
  interactions: ReadonlyArray<Readonly<ToolInteractionUnion>>
): Tool['keys'] =>
  interactions.reduce<Array<any>>((acc, i) => {
    // @ts-expect-error [EN-7967] - TS2339 - Property 'keyboardShortcut' does not exist on type 'Readonly<ToolInteractionUnion>'.
    if (i.type === ToolInteractionType.KeyboardShortcut && i.keyboardShortcut != null) {
      acc.push({
        // @ts-expect-error [EN-7967] - TS2339 - Property 'keyboardShortcut' does not exist on type 'Readonly<ToolInteractionUnion>'.
        key: i.keyboardShortcut,
        interactionId: i.id,
      });
    }
    return acc;
  }, []);

export const getToolById = async (id: string): Promise<Nullable<Tool>> =>
  (await getAllTools()).find((x) => x.id === id);

export function useAllTools(): Nullable<Array<Tool>> {
  const { canCreateAnnotations } = useCheckUserRISPermissions();

  const { value, loading } = useAsync(async () => {
    if (!canCreateAnnotations) {
      return (await getAllTools()).filter((tool) => tool.category !== ToolCategory.Annotations);
    }
    return await getAllTools();
  }, [canCreateAnnotations]);

  return loading ? [] : value;
}

export const useEnabledTools = (): Array<Tool> => {
  const allTools = useAllTools();

  const [flags] = useSplitFlags(
    allTools
      ?.map(({ splitFlag }) => {
        if (splitFlag != null && splitFlag in FF) {
          return splitFlag as FeatureFlag;
        }
        return null;
      })
      .filter((flag): flag is FeatureFlag => flag != null) ?? []
  );

  const enabledTools = useMemo(
    () =>
      allTools?.filter((tool: Tool) => {
        const splitFlag = tool?.splitFlag || '';
        const isEnabled = !splitFlag || flags?.[splitFlag] === 'on';
        return isEnabled;
      }) ?? [],
    [allTools, flags]
  );

  return enabledTools;
};

export function useConfigurableTools(): Array<Tool> {
  const tools = useEnabledTools();
  if (!tools) return [];
  return tools.filter((tool) => tool.source === 'sdk' || configurableTools[tool.name]);
}

export function useVisibilityTools(): Nullable<Array<Tool>> {
  const tools = useEnabledTools();
  if (!tools) return [];
  return tools.filter((tool) => visibilityTools[tool.name]);
}
